import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bot, Send, Plus, User, Loader, Sparkles } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { AI_ASSISTANT } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Why is the order-service in CrashLoopBackOff?",
  "Show me all unhealthy deployments",
  "Which namespace consumes the most resources?",
  "What's causing high memory usage?",
  "Find services without healthy endpoints",
  "Explain recent cluster events",
];

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div data-testid={AI_ASSISTANT.messageItem} className={cn("flex gap-3 animate-fade-in", isUser && "flex-row-reverse")}>
      <div className={cn("w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5",
        isUser ? "bg-slate-700" : "bg-indigo-500/20 border border-indigo-500/30")}>
        {isUser ? <User size={13} className="text-slate-300" /> : <Bot size={13} className="text-indigo-400" />}
      </div>
      <div className={cn("max-w-[78%] rounded-md px-4 py-3 text-sm",
        isUser ? "bg-slate-800 text-slate-200" : "bg-slate-900/80 border border-slate-800 text-slate-200")}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <div className="prose-sm prose-invert" dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }} />
        )}
        <p className={cn("text-[10px] mt-1.5 font-mono", isUser ? "text-slate-500 text-right" : "text-slate-600")}>
          {new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      </div>
    </div>
  );
}

function formatMarkdown(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong class='text-slate-100'>$1</strong>")
    .replace(/`([^`]+)`/g, "<code class='bg-slate-800 text-indigo-300 px-1.5 py-0.5 rounded text-xs font-mono'>$1</code>")
    .replace(/^- (.+)$/gm, "<li class='ml-4 list-disc text-slate-300'>$1</li>")
    .replace(/\n\n/g, "</p><p class='mt-2'>")
    .replace(/\n/g, "<br/>");
}

export default function AIAssistant() {
  const { clusterId } = useCluster();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    inputRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const msg = (text || "").trim() || input.trim();
    if (!msg || loading) return;
    setInput("");

    const userMsg = { role: "user", content: msg, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API}/ai/chat`, {
        message: msg,
        session_id: sessionId,
        cluster_id: clusterId,
      });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, {
        role: "assistant", content: data.response, timestamp: data.timestamp
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: "assistant", content: "Sorry, I encountered an error. Please check the backend connection.",
        timestamp: new Date().toISOString()
      }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div data-testid={AI_ASSISTANT.page} className="flex flex-col" style={{ height: "calc(100vh - 130px)" }}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
            <Sparkles size={14} className="text-indigo-400" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-100" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>KubeAssist</h2>
            <p className="text-[10px] text-slate-500">Powered by Claude Sonnet · {clusterId}</p>
          </div>
        </div>
        <button
          data-testid={AI_ASSISTANT.newChatBtn}
          onClick={startNewChat}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
        >
          <Plus size={12} /> New Chat
        </button>
      </div>

      {/* Chat area */}
      <div data-testid={AI_ASSISTANT.chatContainer} className="flex-1 overflow-y-auto rounded-md border border-slate-800 bg-slate-900/30 p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mb-4">
              <Bot size={22} className="text-indigo-400" />
            </div>
            <h3 className="text-base font-semibold text-slate-200 mb-1" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Ask anything about your cluster</h3>
            <p className="text-sm text-slate-500 max-w-sm mb-6">I have full context of your Kubernetes cluster — pods, deployments, events, alerts, and metrics.</p>
            <div className="grid grid-cols-2 gap-2 w-full max-w-lg">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => sendMessage(s)}
                  className="text-left px-3 py-2 rounded-md border border-slate-800 bg-slate-900/50 text-xs text-slate-400 hover:text-slate-200 hover:border-slate-700 hover:bg-slate-800/50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => <MessageBubble key={i} msg={msg} />)}
        {loading && (
          <div className="flex gap-3 animate-fade-in">
            <div className="w-7 h-7 rounded-md bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center">
              <Bot size={13} className="text-indigo-400" />
            </div>
            <div className="bg-slate-900/80 border border-slate-800 rounded-md px-4 py-3">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex items-end gap-2 mt-3 flex-shrink-0">
        <div className="flex-1 flex items-end bg-slate-900/50 border border-slate-700 rounded-md focus-within:border-indigo-500/60 transition-colors">
          <textarea
            ref={inputRef}
            data-testid={AI_ASSISTANT.chatInput}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about your cluster..."
            rows={1}
            className="flex-1 bg-transparent text-sm text-slate-200 placeholder-slate-500 outline-none px-4 py-3 resize-none max-h-32"
            style={{ lineHeight: "1.5" }}
          />
        </div>
        <button
          data-testid={AI_ASSISTANT.sendBtn}
          onClick={() => sendMessage()}
          disabled={!input.trim() || loading}
          className={cn(
            "p-3 rounded-md transition-all duration-200 flex-shrink-0",
            input.trim() && !loading
              ? "bg-indigo-500 text-white hover:bg-indigo-600 active:scale-95"
              : "bg-slate-800 text-slate-600 cursor-not-allowed"
          )}
        >
          {loading ? <Loader size={15} className="animate-spin" /> : <Send size={15} />}
        </button>
      </div>
      <p className="text-[10px] text-slate-600 mt-1.5 text-center">Press Enter to send · Shift+Enter for new line</p>
    </div>
  );
}
